import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Banknote,
  Calculator,
  Eye,
  FileCheck,
  FileDown,
  FilePlus2,
  Package,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Send,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire, Pastille } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { useAuth } from '../../auth/useAuth';
import { ErreurApi } from '../../lib/api';
import { detailsRefus } from '../../lib/erreurs-api';
import { aujourdhui } from '../../lib/dates-civiles';
import { urlFichierCertificat } from '../../lib/useCertificats';
import {
  urlApercuFacture,
  urlOriginalFacture,
  useCorrigerMontantFacture,
  useEmettreFacture,
  useEtablirAvoir,
  useModifierFacture,
  usePreparerFacture,
  usePreparerFacturePremierEquipement,
  useRecalculerFacture,
  useRetirerReglement,
  useSaisirReglement,
  useSupprimerFacture,
  type Facture,
  type ModeReglement,
  type Reglement,
  type SaisieFacture,
  type SaisieLigneFacture,
  type UniteFacturation,
} from '../../lib/useFactures';
import {
  centimesSaisis,
  formaterCentimes,
  type BlocFinancement,
  type FinancementDossier,
  type PremierEquipement,
} from '../../lib/useFinancements';

export const CLASSE_CHAMP =
  'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

export const CLASSE_LIEN_BOUTON =
  'inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50';

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

function dateHeure(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

/** Jour civil réunionnais d'un instant, `AAAA-MM-JJ`, pour borner un champ date. */
function jourCivil(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Indian/Reunion' }).format(new Date(iso));
}

/** « 10 jours », « 2,5 heures », « 1 forfait », « 3 mois » : l'unité s'accorde à la quantité. */
export function quantiteEtUnite(quantite: string, unite: string): string {
  const nombre = Number(quantite);
  const accordee = nombre >= 2 && !/[sx]$/.test(unite) ? `${unite}s` : unite;
  return `${quantite.replace('.', ',')} ${accordee}`;
}

export function messageRefus(erreur: unknown, defaut: string): string {
  const detail = detailsRefus(erreur)[0];
  if (detail) return detail.message;
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

type Referentiel = FinancementDossier['referentielFacture'];

/**
 * Factures et avoirs d'un contrat (TF-407, sans Tiime). La plateforme les
 * émet elle-même : un brouillon se prépare, se corrige et s'aperçoit ;
 * l'émission lui attribue son numéro, fige ses données et produit l'original,
 * qui ne se modifie plus — une erreur se corrige par un avoir. Les règlements
 * reçus se saisissent sur la facture émise (TF-408), dont l'état — partiellement
 * réglée, réglée, en retard — se déduit ; un règlement saisi par erreur se
 * retire et reste lisible, barré. La facture d'une échéance (TF-410) naît
 * calculée depuis l'échéancier, celle des frais de 1er équipement (TF-411)
 * depuis son encart, à part : elles se recalculent et leur montant se corrige,
 * le calcul restant lisible. Ce qui empêche chaque geste vient de l'API,
 * nommé.
 */
export function FacturesDuContrat({
  bloc,
  emetteur,
  referentiel,
}: {
  bloc: BlocFinancement;
  emetteur: FinancementDossier['emetteur'];
  referentiel: Referentiel;
}): JSX.Element {
  const [preparation, setPreparation] = useState<Facture | 'NOUVELLE' | null>(null);
  const { factures } = bloc;
  const employeur = bloc.contrat.employeur.denomination;

  return (
    <section className="rounded-2xl border border-gray-100" aria-label={`Factures du contrat chez ${employeur}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5 sm:px-4">
        <h5 className="flex min-w-0 items-center gap-2 text-sm font-bold text-codialis-navy">
          <ReceiptText className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
          Factures et avoirs
          {factures.length > 0 && (
            <span className="text-xs font-semibold text-codialis-gray">({factures.length})</span>
          )}
        </h5>
        <BoutonSecondaire onClick={() => setPreparation('NOUVELLE')} disabled={bloc.refusPreparation !== null}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Préparer une facture
        </BoutonSecondaire>
      </div>

      <div className="space-y-3 p-3 sm:p-4">
        {bloc.refusPreparation && <p className="text-xs text-codialis-gray">{bloc.refusPreparation}</p>}
        <AvertissementEmetteur emetteur={emetteur} />

        {bloc.premierEquipement && (
          <FraisPremierEquipement contratId={bloc.contrat.id} equipement={bloc.premierEquipement} />
        )}

        <ListeFactures
          factures={factures}
          modesReglement={referentiel.modesReglement}
          vide="Aucune facture pour ce contrat."
          onModifier={setPreparation}
        />
      </div>

      {preparation && (
        <ModalePreparation
          contratId={bloc.contrat.id}
          employeur={employeur}
          facture={preparation === 'NOUVELLE' ? null : preparation}
          referentiel={referentiel}
          onFermer={() => setPreparation(null)}
        />
      )}
    </section>
  );
}

/** Sans les mentions de l'émetteur, aucune facture ne s'émet : le dire, et mener à leur réglage. */
export function AvertissementEmetteur({ emetteur }: { emetteur: FinancementDossier['emetteur'] }): JSX.Element | null {
  const { peutAcceder } = useAuth();
  if (emetteur.complet || !emetteur.detail) return null;
  return (
    <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
      {emetteur.detail}{' '}
      {peutAcceder('fac-parametres') && (
        <Link
          to="/fac-parametres"
          className="inline-flex min-h-[32px] items-center text-codialis-sky underline-offset-2 hover:underline"
        >
          Régler les mentions de l’émetteur
        </Link>
      )}
    </p>
  );
}

/**
 * Factures et avoirs d'un bloc — un contrat (TF-407) ou l'accord d'un parcours
 * OF (TF-416) — et leurs gestes : aperçu, émission, recalcul, correction du
 * montant, suppression, avoir, règlements. `onModifier` ouvre la correction
 * ligne par ligne d'une facture saisie ; ce qui empêche chaque geste vient de
 * l'API.
 */
export function ListeFactures({
  factures,
  modesReglement,
  vide,
  onModifier,
}: {
  factures: Facture[];
  modesReglement: Referentiel['modesReglement'];
  vide: string;
  onModifier?: (facture: Facture) => void;
}): JSX.Element {
  const [aEmettre, setAEmettre] = useState<Facture | null>(null);
  const [aSupprimer, setASupprimer] = useState<Facture | null>(null);
  const [aAnnuler, setAAnnuler] = useState<Facture | null>(null);
  const [aRegler, setARegler] = useState<Facture | null>(null);
  const [aRetirer, setARetirer] = useState<{ facture: Facture; reglement: Reglement } | null>(null);
  const [aCorriger, setACorriger] = useState<Facture | null>(null);
  const [aRecalculer, setARecalculer] = useState<Facture | null>(null);

  return (
    <>
      {factures.length === 0 ? (
        <p className="text-sm text-codialis-gray">{vide}</p>
      ) : (
        <ul className="space-y-3">
          {factures.map((facture) => (
            <LigneFacture
              key={facture.id}
              facture={facture}
              onModifier={() => onModifier?.(facture)}
              onCorriger={() => setACorriger(facture)}
              onRecalculer={() => setARecalculer(facture)}
              onEmettre={() => setAEmettre(facture)}
              onSupprimer={() => setASupprimer(facture)}
              onAvoir={() => setAAnnuler(facture)}
              onRegler={() => setARegler(facture)}
              onRetirerReglement={(reglement) => setARetirer({ facture, reglement })}
            />
          ))}
        </ul>
      )}

      {aEmettre && <ModaleEmission facture={aEmettre} onFermer={() => setAEmettre(null)} />}
      {aCorriger && <ModaleCorrectionMontant facture={aCorriger} onFermer={() => setACorriger(null)} />}
      {aRecalculer && <ModaleRecalcul facture={aRecalculer} onFermer={() => setARecalculer(null)} />}
      {aSupprimer && <ModaleSuppression facture={aSupprimer} onFermer={() => setASupprimer(null)} />}
      {aAnnuler && <ModaleAvoir facture={aAnnuler} onFermer={() => setAAnnuler(null)} />}
      {aRegler && <ModaleReglement facture={aRegler} modes={modesReglement} onFermer={() => setARegler(null)} />}
      {aRetirer && (
        <ModaleRetraitReglement
          facture={aRetirer.facture}
          reglement={aRetirer.reglement}
          onFermer={() => setARetirer(null)}
        />
      )}
    </>
  );
}

/**
 * Les frais de 1er équipement, facturés à part (TF-411) : montant pris en
 * charge, état, période que portera la facture, et le geste qui la prépare en
 * brouillon — ou ce qui l'en empêche encore.
 */
function FraisPremierEquipement({
  contratId,
  equipement,
}: {
  contratId: string;
  equipement: PremierEquipement;
}): JSX.Element {
  const toast = useToast();
  const preparer = usePreparerFacturePremierEquipement();
  const [erreur, setErreur] = useState<string | null>(null);
  const { preparation } = equipement;
  const refus = preparation?.refus ?? null;
  const montant = equipement.reste?.texte ?? equipement.facture?.texte ?? equipement.frais;

  const lancer = (): void => {
    setErreur(null);
    preparer.mutate(contratId, {
      onSuccess: () => toast('Facture du 1er équipement préparée : vérifiez-la avant de l’émettre.'),
      onError: (motif) => setErreur(messageRefus(motif, 'La facture n’a pas pu être préparée.')),
    });
  };

  return (
    <div
      role="group"
      aria-label="Frais de 1er équipement"
      className="min-w-0 rounded-xl border border-gray-100 bg-codialis-sand px-3 py-2.5 sm:px-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
        <div className="min-w-0 flex-1 basis-56">
          <p className="flex items-center gap-2 text-sm font-bold text-codialis-navy">
            <Package className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
            Frais de 1er équipement
          </p>
          <p className="mt-0.5 text-xs text-codialis-gray">
            Facture à part · période {equipement.periode}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {montant && (
            <span className="font-display text-base font-extrabold tabular-nums text-codialis-navy">{montant}</span>
          )}
          <Pastille texte={equipement.libelle} ton={equipement.ton} />
        </div>
      </div>
      {equipement.facture && equipement.facture.numeros.length > 0 && (
        <p className="mt-1 text-xs text-codialis-gray">
          Facturé {equipement.facture.texte} :{' '}
          <span className="font-mono">{equipement.facture.numeros.join(', ')}</span>
        </p>
      )}
      {equipement.detail && <p className="mt-1 break-words text-xs text-codialis-gray">{equipement.detail}</p>}
      {preparation && (
        <div className="mt-2 flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5">
          {refus && <p className="min-w-0 flex-1 text-xs text-codialis-gray">{refus}</p>}
          <BoutonPrincipal onClick={lancer} disabled={refus !== null || preparer.isPending}>
            <FilePlus2 className="mr-1.5 h-4 w-4" aria-hidden />
            {preparer.isPending ? 'Préparation…' : 'Préparer la facture'}
          </BoutonPrincipal>
        </div>
      )}
      {erreur && (
        <p role="alert" className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700">
          {erreur}
        </p>
      )}
    </div>
  );
}

/** Factures dont la ligne vient d'un calcul : elles se recalculent, et — sauf aux heures — leur montant se corrige. */
const OBJETS_CALCULES = new Set(['ECHEANCE', 'SOLDE', 'PREMIER_EQUIPEMENT', 'HEURES_MENSUELLES', 'FIN_PARCOURS']);

function LigneFacture({
  facture,
  onModifier,
  onCorriger,
  onRecalculer,
  onEmettre,
  onSupprimer,
  onAvoir,
  onRegler,
  onRetirerReglement,
}: {
  facture: Facture;
  onModifier: () => void;
  onCorriger: () => void;
  onRecalculer: () => void;
  onEmettre: () => void;
  onSupprimer: () => void;
  onAvoir: () => void;
  onRegler: () => void;
  onRetirerReglement: (reglement: Reglement) => void;
}): JSX.Element {
  const brouillon = facture.statut === 'BROUILLON';
  const avoir = facture.nature === 'AVOIR';
  const calculee = facture.calcul !== null || OBJETS_CALCULES.has(facture.objet);
  const titre = facture.numero ?? `${facture.natureLibelle} en préparation`;

  return (
    <li className="rounded-xl border border-gray-100 bg-white p-3 sm:p-4">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0">
          <p className="break-words text-sm font-bold text-codialis-navy">
            <span className={facture.numero ? 'font-mono' : ''}>{titre}</span>
            {facture.numero && (
              <span className="ml-2 text-xs font-semibold text-codialis-gray">{facture.natureLibelle}</span>
            )}
          </p>
          <p className="text-xs text-codialis-gray">
            Période {facture.periode.texte}
            {facture.client ? ` · ${facture.client}` : ''}
          </p>
          {facture.emiseLe && (
            <p className="text-[11px] text-codialis-gray">
              Émise le {dateHeure(facture.emiseLe)}
              {facture.emisePar ? ` par ${facture.emisePar}` : ''}
              {facture.dateEcheancePaiement ? ` · échéance ${jourFrancais(facture.dateEcheancePaiement)}` : ''}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="font-display text-base font-extrabold tabular-nums text-codialis-navy">
            {avoir ? `− ${facture.totaux.ttc}` : facture.totaux.ttc}
          </span>
          <Pastille texte={facture.situation.libelle} ton={facture.situation.ton} />
        </div>
      </div>

      {(facture.situation.detail || facture.origine || facture.motif || facture.avoirs.length > 0) && (
        <div className="mt-2 space-y-0.5 text-xs text-codialis-gray">
          {facture.situation.detail && <p>{facture.situation.detail}</p>}
          {facture.origine && (
            <p>
              Annule la facture <span className="font-mono">{facture.origine.numero}</span>
              {facture.motif ? ` — ${facture.motif}` : ''}
            </p>
          )}
          {facture.avoirs.length > 0 && (
            <p>
              Avoir{facture.avoirs.length > 1 ? 's' : ''} :{' '}
              <span className="font-mono">{facture.avoirs.map((un) => un.numero).join(', ')}</span>
            </p>
          )}
        </div>
      )}

      <ul className="mt-2 space-y-1 border-t border-gray-100 pt-2">
        {facture.lignes.map((ligne, rang) => (
          <li key={rang} className="flex items-baseline justify-between gap-3 text-xs">
            <span className="min-w-0 break-words text-codialis-navy">
              {ligne.libelle}
              <span className="text-codialis-gray">
                {' '}
                · {quantiteEtUnite(ligne.quantite, ligne.uniteLibelle)}
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-codialis-navy">
              {formaterCentimes(Math.round(Number(ligne.montant) * 100))}
            </span>
          </li>
        ))}
      </ul>

      {facture.calcul && (
        <div className="mt-2 rounded-lg bg-codialis-sand px-3 py-2 text-[11px] text-codialis-gray">
          <p className="flex gap-1.5">
            <Calculator className="mt-px h-3.5 w-3.5 shrink-0 text-codialis-sky" aria-hidden />
            <span className="min-w-0 break-words">
              <span className="font-semibold text-codialis-navy">{facture.calcul.titre} : </span>
              {facture.calcul.detail}
            </span>
          </p>
          {facture.calcul.corrige && (
            <p className="mt-1 font-semibold text-amber-800">
              Montant corrigé à la main : le calcul proposait {facture.calcul.propose}.
            </p>
          )}
        </div>
      )}

      {facture.certificat && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px]">
          <p
            className={`flex min-w-0 flex-1 gap-1.5 ${
              facture.certificat.id ? 'text-codialis-gray' : 'font-semibold text-amber-800'
            }`}
          >
            <FileCheck className="mt-px h-3.5 w-3.5 shrink-0 text-codialis-sky" aria-hidden />
            <span className="min-w-0 break-words">{facture.certificat.texte}</span>
          </p>
          {facture.certificat.id && (
            <a
              href={urlFichierCertificat(facture.certificat.id)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 font-semibold text-codialis-sky hover:bg-codialis-sky/5"
            >
              <FileDown className="h-3.5 w-3.5" aria-hidden />
              Certificat
            </a>
          )}
        </div>
      )}

      {facture.solde && (
        <Reglements facture={facture} solde={facture.solde} onRetirer={onRetirerReglement} />
      )}

      {brouillon && facture.refus.emission && (
        <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
          {facture.refus.emission}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {brouillon && (
          <>
            <a href={urlApercuFacture(facture.id)} target="_blank" rel="noreferrer" className={CLASSE_LIEN_BOUTON}>
              <Eye className="mr-1.5 h-4 w-4" aria-hidden />
              Aperçu
            </a>
            <BoutonSecondaire onClick={onSupprimer} disabled={facture.refus.suppression !== null}>
              <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
              Supprimer
            </BoutonSecondaire>
            {calculee ? (
              <>
                <BoutonSecondaire onClick={onRecalculer} disabled={facture.refus.recalcul !== null}>
                  <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden />
                  Recalculer
                </BoutonSecondaire>
                {facture.refus.correction === null && (
                  <BoutonSecondaire onClick={onCorriger} disabled={!facture.calcul}>
                    <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
                    Corriger le montant
                  </BoutonSecondaire>
                )}
              </>
            ) : (
              <BoutonSecondaire onClick={onModifier} disabled={facture.refus.modification !== null}>
                <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
                Modifier
              </BoutonSecondaire>
            )}
            <BoutonPrincipal onClick={onEmettre} disabled={facture.refus.emission !== null}>
              <Send className="mr-1.5 h-4 w-4" aria-hidden />
              Émettre
            </BoutonPrincipal>
          </>
        )}
        {facture.fichier && (
          <a href={urlOriginalFacture(facture.id)} target="_blank" rel="noreferrer" className={CLASSE_LIEN_BOUTON}>
            <FileDown className="mr-1.5 h-4 w-4" aria-hidden />
            Télécharger
          </a>
        )}
        {!brouillon && !avoir && facture.refus.avoir === null && (
          <BoutonSecondaire onClick={onAvoir}>
            <Undo2 className="mr-1.5 h-4 w-4" aria-hidden />
            Établir un avoir
          </BoutonSecondaire>
        )}
        {!brouillon && !avoir && facture.refus.reglement === null && (
          <BoutonPrincipal onClick={onRegler}>
            <Banknote className="mr-1.5 h-4 w-4" aria-hidden />
            Saisir un règlement
          </BoutonPrincipal>
        )}
      </div>
    </li>
  );
}

/** Règlements reçus sur une facture émise, retirés compris, et ce qui reste dû. */
export function Reglements({
  facture,
  solde,
  onRetirer,
}: {
  facture: Facture;
  solde: NonNullable<Facture['solde']>;
  onRetirer: (reglement: Reglement) => void;
}): JSX.Element {
  const tropPercu = solde.resteDuCentimes < 0;
  return (
    <div
      role="group"
      aria-label={`Règlements de la facture ${facture.numero ?? ''}`}
      className="mt-2 border-t border-gray-100 pt-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <p className="text-xs font-semibold text-codialis-navy">Règlements</p>
        <p className="text-xs tabular-nums text-codialis-gray">
          Réglé <span className="font-semibold text-codialis-navy">{solde.regle}</span>
          {' · '}
          {tropPercu ? 'trop-perçu ' : 'reste dû '}
          <span className="font-semibold text-codialis-navy">
            {tropPercu ? formaterCentimes(-solde.resteDuCentimes) : solde.resteDu}
          </span>
        </p>
      </div>
      {facture.reglements.length === 0 ? (
        <p className="mt-1 text-xs text-codialis-gray">Aucun règlement reçu.</p>
      ) : (
        <ul className="mt-1 divide-y divide-gray-50">
          {facture.reglements.map((reglement) => {
            const retire = reglement.retrait !== null;
            return (
              <li key={reglement.id} className="flex items-start justify-between gap-3 py-1.5 text-xs">
                <div className="min-w-0">
                  <p className={`break-words ${retire ? 'text-codialis-gray line-through' : 'text-codialis-navy'}`}>
                    {jourFrancais(reglement.date)} · {reglement.modeLibelle}
                    {reglement.reference ? ` · ${reglement.reference}` : ''}
                  </p>
                  {reglement.commentaire && (
                    <p className="break-words text-codialis-gray">{reglement.commentaire}</p>
                  )}
                  <p className="text-[11px] text-codialis-gray">
                    Saisi le {dateHeure(reglement.saisiLe)}
                    {reglement.saisiPar ? ` par ${reglement.saisiPar}` : ''}
                  </p>
                  {reglement.retrait && (
                    <p className="break-words text-[11px] font-semibold text-codialis-gray">
                      Retiré le {dateHeure(reglement.retrait.le)}
                      {reglement.retrait.par ? ` par ${reglement.retrait.par}` : ''} — {reglement.retrait.motif}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <span
                    className={`tabular-nums ${retire ? 'text-codialis-gray line-through' : 'font-semibold text-codialis-navy'}`}
                  >
                    {reglement.montant}
                  </span>
                  {reglement.refusRetrait === null && (
                    <button
                      type="button"
                      onClick={() => onRetirer(reglement)}
                      aria-label={`Retirer le règlement du ${jourFrancais(reglement.date)} de ${reglement.montant}`}
                      title="Retirer ce règlement saisi par erreur"
                      className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg text-codialis-gray transition hover:bg-gray-50 hover:text-red-700"
                    >
                      <X className="h-4 w-4" aria-hidden />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Préparer ou corriger un brouillon
// ---------------------------------------------------------------------------

const LIGNE_VIDE: SaisieLigneFacture = {
  libelle: '',
  quantite: '1',
  unite: 'FORFAIT',
  prixUnitaire: '',
  tauxTva: '0',
};

function enSaisie(valeur: string | null): string {
  return valeur === null ? '' : valeur.replace('.', ',');
}

/** Aperçu du montant pendant la saisie ; l'API le recalcule à l'enregistrement. */
function montantSaisi(ligne: SaisieLigneFacture): number | undefined {
  const quantite = Number(ligne.quantite.replace(/[\s  ]/g, '').replace(',', '.'));
  const prix = Number(ligne.prixUnitaire.replace(/[\s  ]/g, '').replace(',', '.').replace(/€$/, ''));
  if (ligne.prixUnitaire.trim() === '' || !Number.isFinite(quantite) || !Number.isFinite(prix)) return undefined;
  return Math.round(quantite * prix * 100);
}

function ModalePreparation({
  contratId,
  employeur,
  facture,
  referentiel,
  onFermer,
}: {
  contratId: string;
  employeur: string;
  facture: Facture | null;
  referentiel: Referentiel;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const preparer = usePreparerFacture();
  const modifier = useModifierFacture();
  const enCours = preparer.isPending || modifier.isPending;
  const [debut, setDebut] = useState(facture?.periode.debut ?? '');
  const [fin, setFin] = useState(facture?.periode.fin ?? '');
  const [lignes, setLignes] = useState<SaisieLigneFacture[]>(() =>
    facture
      ? facture.lignes.map((ligne) => ({
          libelle: ligne.libelle,
          quantite: enSaisie(ligne.quantite),
          unite: ligne.unite,
          prixUnitaire: enSaisie(ligne.prixUnitaire),
          tauxTva: String(Number(ligne.tauxTva)),
        }))
      : [{ ...LIGNE_VIDE }],
  );
  const [erreur, setErreur] = useState<string | null>(null);

  const montants = lignes.map(montantSaisi);
  const totalHt = montants.every((montant) => montant !== undefined)
    ? montants.reduce<number>((somme, montant) => somme + (montant ?? 0), 0)
    : null;

  const changer = (rang: number, champ: keyof SaisieLigneFacture, valeur: string): void => {
    setLignes((avant) => avant.map((ligne, index) => (index === rang ? { ...ligne, [champ]: valeur } : ligne)));
    setErreur(null);
  };

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const saisie: SaisieFacture = { periodeDebut: debut, periodeFin: fin, lignes };
    const rappels = {
      onSuccess: () => {
        toast(facture ? 'Brouillon corrigé.' : 'Facture préparée : vérifiez l’aperçu avant de l’émettre.');
        onFermer();
      },
      onError: (refus: unknown) => setErreur(messageRefus(refus, 'La facture n’a pas pu être enregistrée.')),
    };
    if (facture) modifier.mutate({ id: facture.id, saisie }, rappels);
    else preparer.mutate({ contratId, saisie }, rappels);
  };

  return (
    <Modale
      ouverte
      titre={facture ? 'Corriger le brouillon' : 'Préparer une facture'}
      sousTitre={`Contrat chez ${employeur} · le client est le payeur du bloc Financement`}
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <form onSubmit={soumettre} className="space-y-5 p-4 sm:p-6" aria-label="Saisie de la facture">
        <fieldset className="min-w-0">
          <legend className="mb-2 text-xs font-semibold text-codialis-navy">Période facturée</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Champ libelle="Du" id="facture-debut">
              <input
                id="facture-debut"
                type="date"
                required
                value={debut}
                onChange={(e) => {
                  setDebut(e.target.value);
                  setErreur(null);
                }}
                className={CLASSE_CHAMP}
              />
            </Champ>
            <Champ libelle="Au" id="facture-fin">
              <input
                id="facture-fin"
                type="date"
                required
                value={fin}
                min={debut || undefined}
                onChange={(e) => {
                  setFin(e.target.value);
                  setErreur(null);
                }}
                className={CLASSE_CHAMP}
              />
            </Champ>
          </div>
        </fieldset>

        <fieldset className="min-w-0 space-y-3">
          <legend className="mb-2 text-xs font-semibold text-codialis-navy">Lignes</legend>
          {lignes.map((ligne, rang) => {
            const montant = montants[rang];
            return (
              <div key={rang} className="min-w-0 space-y-3 rounded-xl border border-gray-100 bg-white p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold text-codialis-gray">Ligne {rang + 1}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs tabular-nums text-codialis-navy">
                      {montant === undefined ? '—' : `${formaterCentimes(montant)} HT`}
                    </span>
                    {lignes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setLignes((avant) => avant.filter((_, index) => index !== rang))}
                        aria-label={`Retirer la ligne ${rang + 1}`}
                        className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg text-codialis-gray transition hover:bg-gray-50 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    )}
                  </div>
                </div>
                <Champ libelle="Désignation" id={`ligne-${rang}-libelle`}>
                  <input
                    id={`ligne-${rang}-libelle`}
                    required
                    maxLength={300}
                    value={ligne.libelle}
                    placeholder="Contrat d’apprentissage — coût pédagogique, échéance n° 1"
                    onChange={(e) => changer(rang, 'libelle', e.target.value)}
                    className={CLASSE_CHAMP}
                  />
                </Champ>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Champ libelle="Quantité" id={`ligne-${rang}-quantite`}>
                    <input
                      id={`ligne-${rang}-quantite`}
                      required
                      inputMode="decimal"
                      value={ligne.quantite}
                      onChange={(e) => changer(rang, 'quantite', e.target.value)}
                      className={`${CLASSE_CHAMP} text-right tabular-nums`}
                    />
                  </Champ>
                  <Champ libelle="Unité" id={`ligne-${rang}-unite`}>
                    <select
                      id={`ligne-${rang}-unite`}
                      value={ligne.unite}
                      onChange={(e) => changer(rang, 'unite', e.target.value as UniteFacturation)}
                      className={CLASSE_CHAMP}
                    >
                      {referentiel.unites.map((unite) => (
                        <option key={unite.valeur} value={unite.valeur}>
                          {unite.libelle}
                        </option>
                      ))}
                    </select>
                  </Champ>
                  <Champ libelle="Prix unitaire HT" id={`ligne-${rang}-prix`}>
                    <input
                      id={`ligne-${rang}-prix`}
                      required
                      inputMode="decimal"
                      value={ligne.prixUnitaire}
                      placeholder="616,00"
                      onChange={(e) => changer(rang, 'prixUnitaire', e.target.value)}
                      className={`${CLASSE_CHAMP} text-right tabular-nums`}
                    />
                  </Champ>
                  <Champ libelle="TVA" id={`ligne-${rang}-tva`}>
                    <select
                      id={`ligne-${rang}-tva`}
                      value={ligne.tauxTva}
                      onChange={(e) => changer(rang, 'tauxTva', e.target.value)}
                      className={CLASSE_CHAMP}
                    >
                      {referentiel.tauxTva.map((taux) => (
                        <option key={taux.valeur} value={taux.valeur}>
                          {taux.libelle}
                        </option>
                      ))}
                    </select>
                  </Champ>
                </div>
              </div>
            );
          })}
          {lignes.length < 30 && (
            <BoutonSecondaire onClick={() => setLignes((avant) => [...avant, { ...LIGNE_VIDE }])}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              Ajouter une ligne
            </BoutonSecondaire>
          )}
        </fieldset>

        <div
          className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-2xl bg-codialis-sand px-3 py-3 sm:px-4"
          aria-live="polite"
        >
          <span className="text-sm font-bold text-codialis-navy">Total HT</span>
          <span className="font-display text-lg font-extrabold tabular-nums text-codialis-navy">
            {totalHt === null ? '—' : formaterCentimes(totalHt)}
          </span>
          <span className="w-full text-[11px] text-codialis-gray">
            Montants recalculés par la plateforme à l’enregistrement. Le nom de l’apprenti, la
            formation, ses dates, les heures réalisées et la référence DK sont portés d’office.
          </span>
        </div>

        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={enCours}>
            {enCours ? 'Enregistrement…' : facture ? 'Enregistrer' : 'Préparer le brouillon'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

export function Champ({ libelle, id, children }: { libelle: string; id: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-[11px] font-semibold text-codialis-gray">
        {libelle}
      </label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Émettre, supprimer, annuler
// ---------------------------------------------------------------------------

function ModaleEmission({ facture, onFermer }: { facture: Facture; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const emettre = useEmettreFacture();
  const [erreur, setErreur] = useState<string | null>(null);

  const confirmer = (): void => {
    setErreur(null);
    emettre.mutate(facture.id, {
      onSuccess: ({ facture: emise }) => {
        toast(`Facture ${emise.numero ?? ''} émise.`);
        onFermer();
      },
      onError: (refus) => setErreur(messageRefus(refus, 'La facture n’a pas pu être émise.')),
    });
  };

  return (
    <Modale ouverte titre="Émettre la facture" onFermer={onFermer} largeur="max-w-lg">
      <div className="space-y-4 p-4 sm:p-6">
        <dl className="grid grid-cols-1 gap-3 rounded-2xl bg-codialis-sand p-3 text-sm sm:grid-cols-2 sm:p-4">
          <div className="min-w-0">
            <dt className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Client</dt>
            <dd className="break-words font-medium text-codialis-navy">{facture.client ?? '—'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Montant TTC</dt>
            <dd className="font-medium tabular-nums text-codialis-navy">{facture.totaux.ttc}</dd>
          </div>
          <div className="min-w-0 sm:col-span-2">
            <dt className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Période</dt>
            <dd className="font-medium text-codialis-navy">{facture.periode.texte}</dd>
          </div>
        </dl>
        <p className="text-sm text-codialis-navy">
          La facture reçoit son numéro, ses données sont figées et son PDF devient l’original. Elle ne
          pourra plus être modifiée ni supprimée : une erreur se corrigera par un avoir.
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={emettre.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal onClick={confirmer} disabled={emettre.isPending}>
            {emettre.isPending ? 'Émission…' : 'Émettre la facture'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}

/**
 * Corriger une facture calculée : son libellé et son montant HT, rien
 * d'autre — sa période et son unique ligne viennent de son calcul. Le calcul
 * reste lisible à côté.
 */
function ModaleCorrectionMontant({ facture, onFermer }: { facture: Facture; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const corriger = useCorrigerMontantFacture();
  const ligne = facture.lignes[0];
  const [libelle, setLibelle] = useState(ligne?.libelle ?? '');
  const [montant, setMontant] = useState(ligne ? ligne.montant.replace('.', ',') : '');
  const [erreur, setErreur] = useState<string | null>(null);
  const lu = centimesSaisis(montant);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    corriger.mutate(
      { id: facture.id, libelle, montant },
      {
        onSuccess: () => {
          toast('Montant corrigé.');
          onFermer();
        },
        onError: (refus) => setErreur(messageRefus(refus, 'Le montant n’a pas pu être corrigé.')),
      },
    );
  };

  return (
    <Modale
      ouverte
      titre="Corriger le montant"
      sousTitre={`${facture.objetLibelle} · période ${facture.periode.texte}`}
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6" aria-label="Correction du montant">
        {facture.calcul && (
          <div className="rounded-2xl bg-codialis-sand p-3 text-xs text-codialis-gray sm:p-4">
            <p className="font-semibold text-codialis-navy">Le calcul propose {facture.calcul.propose}</p>
            <p className="mt-0.5 break-words">{facture.calcul.detail}</p>
          </div>
        )}
        <Champ libelle="Désignation" id="correction-libelle">
          <input
            id="correction-libelle"
            required
            maxLength={300}
            value={libelle}
            onChange={(e) => {
              setLibelle(e.target.value);
              setErreur(null);
            }}
            className={CLASSE_CHAMP}
          />
        </Champ>
        <Champ libelle="Montant HT" id="correction-montant">
          <div className="relative">
            <input
              id="correction-montant"
              required
              inputMode="decimal"
              autoComplete="off"
              value={montant}
              aria-invalid={lu === undefined}
              onChange={(e) => {
                setMontant(e.target.value);
                setErreur(null);
              }}
              className={`${CLASSE_CHAMP} pr-8 text-right tabular-nums ${lu === undefined ? 'border-red-300 bg-red-50' : ''}`}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-codialis-gray"
            >
              €
            </span>
          </div>
        </Champ>
        <p className="text-[11px] text-codialis-gray">
          {facture.objet === 'ECHEANCE'
            ? 'Un montant corrigé ne s’explique plus par le prix d’un mois : la facture n’imprime alors ni le prix unitaire ni le détail du calcul. '
            : facture.objet === 'SOLDE'
              ? 'Un montant corrigé ne s’explique plus par le prix d’un jour ouvré : la facture n’imprime alors ni le prix unitaire ni le détail du calcul. '
              : 'Un montant corrigé ne s’explique plus par les frais pris en charge : la facture n’imprime alors plus le détail du calcul. '}
          {'«\u00a0Recalculer\u00a0»'} rétablit la proposition.
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={corriger.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={corriger.isPending || lu === undefined}>
            {corriger.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

/**
 * Recalculer une facture calculée sur les données du jour : coût pris en
 * charge, dates du contrat, déjà facturé pour une échéance ; fin du parcours
 * et jours ouvrés en plus pour le solde ; frais, période et déjà facturé pour
 * le 1er équipement. Un montant corrigé est remplacé.
 */
function ModaleRecalcul({ facture, onFermer }: { facture: Facture; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const recalculer = useRecalculerFacture();
  const [erreur, setErreur] = useState<string | null>(null);

  const confirmer = (): void => {
    setErreur(null);
    recalculer.mutate(facture.id, {
      onSuccess: ({ facture: relue }) => {
        toast(`Facture recalculée : ${relue.totaux.ttc}.`);
        onFermer();
      },
      onError: (refus) => setErreur(messageRefus(refus, 'La facture n’a pas pu être recalculée.')),
    });
  };

  return (
    <Modale ouverte titre="Recalculer la facture" onFermer={onFermer} largeur="max-w-lg">
      <div className="space-y-4 p-4 sm:p-6">
        {facture.calcul?.aRevoir && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
            {facture.calcul.aRevoir}
          </p>
        )}
        <p className="text-sm text-codialis-navy">
          {facture.objet === 'PREMIER_EQUIPEMENT'
            ? 'Le montant est recalculé sur les données du jour : frais de 1er équipement pris en charge, période de la première échéance et factures déjà émises.'
            : facture.objet === 'SOLDE'
              ? 'Le solde est recalculé sur les données du jour : coût pédagogique pris en charge, jours ouvrés du contrat et du parcours jusqu’à sa fin ou sa rupture, et factures déjà émises.'
              : 'Le montant est recalculé sur les données du jour : coût pédagogique pris en charge, dates du contrat et factures déjà émises.'}
          {facture.calcul?.corrige ? ` Le montant corrigé à la main (${facture.totaux.ht} HT) sera remplacé.` : ''}
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={recalculer.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal onClick={confirmer} disabled={recalculer.isPending}>
            {recalculer.isPending ? 'Recalcul…' : 'Recalculer'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}

function ModaleSuppression({ facture, onFermer }: { facture: Facture; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const supprimer = useSupprimerFacture();
  const [erreur, setErreur] = useState<string | null>(null);

  return (
    <Modale ouverte titre="Supprimer le brouillon" onFermer={onFermer} largeur="max-w-lg">
      <div className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-navy">
          Le brouillon de la période {facture.periode.texte} ({facture.totaux.ttc}) sera supprimé. Il
          n’a pas de numéro : la numérotation n’en garde aucune trace.
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={supprimer.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal
            onClick={() =>
              supprimer.mutate(facture.id, {
                onSuccess: () => {
                  toast('Brouillon supprimé.');
                  onFermer();
                },
                onError: (refus) => setErreur(messageRefus(refus, 'Le brouillon n’a pas pu être supprimé.')),
              })
            }
            disabled={supprimer.isPending}
          >
            {supprimer.isPending ? 'Suppression…' : 'Supprimer'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}

function ModaleAvoir({ facture, onFermer }: { facture: Facture; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const etablir = useEtablirAvoir();
  const [motif, setMotif] = useState('');
  const [partiel, setPartiel] = useState(false);
  const [montant, setMontant] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const lu = centimesSaisis(montant);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (partiel && (lu === undefined || lu === null)) {
      setErreur('Un montant s’écrit en euros, deux décimales au plus : 152,00.');
      return;
    }
    setErreur(null);
    etablir.mutate(
      { id: facture.id, motif: motif.trim(), montant: partiel ? montant : null },
      {
        onSuccess: ({ avoir }) => {
          toast(`Avoir ${avoir.numero ?? ''} émis.`);
          onFermer();
        },
        onError: (refus) => setErreur(messageRefus(refus, 'L’avoir n’a pas pu être émis.')),
      },
    );
  };

  return (
    <Modale
      ouverte
      titre="Établir un avoir"
      sousTitre={`Sur la facture ${facture.numero ?? ''} · reste à annuler ${facture.avoirable.texte}`}
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6" aria-label="Saisie de l’avoir">
        <Champ libelle="Motif de l’avoir" id="avoir-motif">
          <textarea
            id="avoir-motif"
            required
            minLength={3}
            maxLength={500}
            rows={3}
            value={motif}
            onChange={(e) => {
              setMotif(e.target.value);
              setErreur(null);
            }}
            className={`${CLASSE_CHAMP} py-2`}
          />
        </Champ>
        <fieldset className="min-w-0 space-y-2">
          <legend className="mb-1 text-[11px] font-semibold text-codialis-gray">Montant annulé</legend>
          <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3">
            <input type="radio" name="avoir-portee" checked={!partiel} onChange={() => setPartiel(false)} />
            <span className="min-w-0 text-sm text-codialis-navy">
              Tout ce qui reste à annuler ({facture.avoirable.texte})
            </span>
          </label>
          <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3">
            <input type="radio" name="avoir-portee" checked={partiel} onChange={() => setPartiel(true)} />
            <span className="min-w-0 text-sm text-codialis-navy">Une partie, TTC</span>
          </label>
          {partiel && (
            <div className="relative">
              <input
                aria-label="Montant TTC de l’avoir"
                inputMode="decimal"
                required
                value={montant}
                placeholder="152,00"
                onChange={(e) => {
                  setMontant(e.target.value);
                  setErreur(null);
                }}
                aria-invalid={lu === undefined}
                className={`${CLASSE_CHAMP} pr-8 text-right tabular-nums`}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-codialis-gray"
              >
                €
              </span>
            </div>
          )}
        </fieldset>
        <p className="text-xs text-codialis-gray">
          L’avoir est numéroté et émis aussitôt. Il reprend le client et les mentions de la facture.
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={etablir.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={etablir.isPending}>
            {etablir.isPending ? 'Émission…' : 'Émettre l’avoir'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Règlements
// ---------------------------------------------------------------------------

/** « 848,00 » : le reste dû tel qu'on le saisit. */
function centimesEnSaisie(centimes: number): string {
  return (centimes / 100).toFixed(2).replace('.', ',');
}

export function ModaleReglement({
  facture,
  modes,
  onFermer,
}: {
  facture: Facture;
  modes: Referentiel['modesReglement'];
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const saisir = useSaisirReglement();
  const resteDu = facture.solde?.resteDuCentimes ?? 0;
  const [date, setDate] = useState(aujourdhui());
  const [montant, setMontant] = useState(resteDu > 0 ? centimesEnSaisie(resteDu) : '');
  const [mode, setMode] = useState<ModeReglement>(modes[0]?.valeur ?? 'VIREMENT');
  const [reference, setReference] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const lu = centimesSaisis(montant);
  const emiseLe = facture.emiseLe ? jourCivil(facture.emiseLe) : undefined;

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (lu === undefined || lu === null) {
      setErreur('Un montant s’écrit en euros, deux décimales au plus : 848,00.');
      return;
    }
    setErreur(null);
    saisir.mutate(
      { id: facture.id, saisie: { dateReglement: date, montant, mode, reference, commentaire } },
      {
        onSuccess: ({ facture: reglee }) => {
          toast(`Règlement enregistré : ${reglee.situation.libelle.toLowerCase()}.`);
          onFermer();
        },
        onError: (refus) => setErreur(messageRefus(refus, 'Le règlement n’a pas pu être enregistré.')),
      },
    );
  };

  return (
    <Modale
      ouverte
      titre="Saisir un règlement"
      sousTitre={`Facture ${facture.numero ?? ''} · reste dû ${facture.solde?.resteDu ?? '—'}`}
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6" aria-label="Saisie du règlement">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle="Reçu le" id="reglement-date">
            <input
              id="reglement-date"
              type="date"
              required
              value={date}
              min={emiseLe}
              max={aujourdhui()}
              onChange={(e) => {
                setDate(e.target.value);
                setErreur(null);
              }}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Montant reçu" id="reglement-montant">
            <div className="relative">
              <input
                id="reglement-montant"
                inputMode="decimal"
                required
                value={montant}
                placeholder="848,00"
                onChange={(e) => {
                  setMontant(e.target.value);
                  setErreur(null);
                }}
                aria-invalid={lu === undefined}
                className={`${CLASSE_CHAMP} pr-8 text-right tabular-nums`}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-codialis-gray"
              >
                €
              </span>
            </div>
          </Champ>
          <Champ libelle="Mode" id="reglement-mode">
            <select
              id="reglement-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as ModeReglement)}
              className={CLASSE_CHAMP}
            >
              {modes.map((un) => (
                <option key={un.valeur} value={un.valeur}>
                  {un.libelle}
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Référence (facultative)" id="reglement-reference">
            <input
              id="reglement-reference"
              maxLength={100}
              value={reference}
              placeholder="Référence du virement, n° du chèque"
              onChange={(e) => setReference(e.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        <Champ libelle="Commentaire (facultatif)" id="reglement-commentaire">
          <textarea
            id="reglement-commentaire"
            maxLength={500}
            rows={2}
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            className={`${CLASSE_CHAMP} py-2`}
          />
        </Champ>
        <p className="text-xs text-codialis-gray">
          Un virement qui règle plusieurs factures se saisit facture par facture, avec la même référence.
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={saisir.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={saisir.isPending}>
            {saisir.isPending ? 'Enregistrement…' : 'Enregistrer le règlement'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

export function ModaleRetraitReglement({
  facture,
  reglement,
  onFermer,
}: {
  facture: Facture;
  reglement: Reglement;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const retirer = useRetirerReglement();
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    retirer.mutate(
      { id: facture.id, reglementId: reglement.id, motif: motif.trim() },
      {
        onSuccess: () => {
          toast('Règlement retiré.');
          onFermer();
        },
        onError: (refus) => setErreur(messageRefus(refus, 'Le règlement n’a pas pu être retiré.')),
      },
    );
  };

  return (
    <Modale
      ouverte
      titre="Retirer le règlement"
      sousTitre={`Facture ${facture.numero ?? ''}`}
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6" aria-label="Retrait du règlement">
        <p className="text-sm text-codialis-navy">
          Le règlement de {reglement.montant} reçu le {jourFrancais(reglement.date)} ({reglement.modeLibelle}
          {reglement.reference ? `, ${reglement.reference}` : ''}) ne comptera plus. Sa ligne reste visible,
          barrée, avec le motif. Une saisie juste se refait ensuite.
        </p>
        <Champ libelle="Motif du retrait" id="retrait-motif">
          <textarea
            id="retrait-motif"
            required
            minLength={3}
            maxLength={500}
            rows={3}
            value={motif}
            placeholder="Montant mal saisi, virement d’un autre dossier…"
            onChange={(e) => {
              setMotif(e.target.value);
              setErreur(null);
            }}
            className={`${CLASSE_CHAMP} py-2`}
          />
        </Champ>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={retirer.isPending}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={retirer.isPending}>
            {retirer.isPending ? 'Retrait…' : 'Retirer le règlement'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
